/* eslint-disable  @typescript-eslint/no-explicit-any */
import axios from '@/plugins/axios'
import { Module } from 'vuex'
import { sortResults, sortCollection } from '@/utils/collections'

export interface Feed {
    username: string
    token: string
}

const feedModule: Module<any, any> = {
    state: {
        feeds: [],
        totalRows: 0,
    },

    actions: {
        async initFeedIndex() {
            if (this.feedIndexLoading) {
        		await this.initFeedromise
        		return
        	}
            this.feedIndexLoading = true
            this.initFeedromise = this.dispatch('initIndex', {
                id: 'feed',
                idField: 'id',
                indexedFields: [
                    'name',
                    'url',
                ],
                source:'/api/feeds',
                refresh: true,
            })
            await this.initFeedromise
            this.feedIndexLoading = false
            this.initFeedromise = null
        },
        
        async getFeeds(_, params) {
            const indexExists = await this.dispatch('indexExists', 'feed')
            if (!indexExists || params?.refresh) {
                await this.dispatch('initFeedIndex')
            }

            const feedIdsFilter = (items) => {
                const filter = params.feedIds
                return filter
                    ? items?.filter((e) => filter.includes(e.id) || filter.includes(e.id.toString()))
                    : items
            }

            const feedIdsExcludesFilter = (items) => {
                const filter = params.excludes
                return filter
                    ? items?.filter((e) => !filter.includes(e.id.toString()))
                    : items
            }

            const statusFilter = (items) => {
                const filter = params.status
                return filter
                    ? items?.filter((e) => e.deleted !== (filter === 'active'))
                    : items?.filter((e) => !e.deleted)
            }

            const approvalTypeFilter = (items) => {
                const filter = params.autoApprove
                return filter
                    ? items?.filter((e) => e.autoApprove === (filter === 'auto'))
                    : items
            }

            const sourceFilter = (items) => {
                const filter = params.source
                return filter
                    ? items?.filter((e) => e?.source?.type === filter)
                    : items
            }

            const createdByFilter = (items) => {
                const filters = params.createdByIds
            
                return filters && filters.length
                    ? items?.filter((e) => filters.includes(e.createdByWorkspaceMemberId))
                    : items
            }

            const sortName = (items) => {
                return sortCollection(items, 'name', params?.sortOrder?.toLowerCase() === 'asc')
            }

            const sortSource = (items) => {
                return sortCollection(items, 'source.type', params?.sortOrder?.toLowerCase() === 'asc')
            }

            const sortLastPull = (items) => {
                return sortResults(items, 'lastPulledAt', params?.sortOrder)
            }

            const sortContentCount = (items) => {
                return sortResults(items, 'contentCount', params?.sortOrder)
            }

            const sortApprovalType = (items) => {
                return sortResults(items, 'autoApprove', params?.sortOrder)
            }

            const getSortOrder = () => {
                if (params?.sortBy === 'source') {
                    return sortSource
                } else if (params?.sortBy === 'lastPulledAt') {
                    return sortLastPull
                } else if (params?.sortBy === 'contentCount') {
                    return sortContentCount
                } else if (params?.sortBy === 'autoApprove') {
                    return sortApprovalType
                } else {
                    return sortName
                }
            }

            let records = null
            records = await this.dispatch('getRecords', {
                index: 'feed',
                term: params?.search,
                fields: ['name'],
                page: params?.page ?? 1,
                perPage: params?.perPage ?? 10,
                filters: [feedIdsFilter, feedIdsExcludesFilter, statusFilter, approvalTypeFilter, sourceFilter, createdByFilter],
                sort: getSortOrder(),
            })

            return records
        },

        async getFeed(_, { id }) {
            if (id === undefined || id === null) {
                throw new Error(`Invalid Content Source ID specified: ${id}`)
            }

            const indexExists = await this.dispatch('indexExists', 'feed')

            if (indexExists) {
                return await this.dispatch('getRecord', {
                    index: 'feed',
                    id,
                })
            } else {
                return await this.dispatch('getFeedFresh', id)
            }
        },

        async getFeedFresh(_, id) {
            const indexExists = await this.dispatch('indexExists', 'feed')

            const { data } = await axios.get(`/api/feeds/${id}`)
            if (!indexExists) {
                await this.dispatch('initFeedIndex')
            }

            if (data) {
                await this.dispatch('addRecord', {
                    index: 'feed',
                    record: data,
                })
            }

            return data
        },

        async setFeedContentCount(_, params) {
            if (params.id === undefined || params.id === null) {
                throw new Error(`Invalid Feed ID specified: ${params.id}`)
            }

            const indexExists = await this.dispatch('indexExists', 'feed')

            if (indexExists) {
                const feed = await this.dispatch('getRecord', {
                    index: 'feed',
                    id: params.id,
                })
                if (feed) {
                    if (params.value) {
                        if (params?.status==='active') {
                            feed.contentCountByStatus.active = params.value
                        } else if (params?.status==='inreview') {
                            feed.contentCountByStatus.inreview = params.value
                        } else if (params?.status==='draft') {
                            feed.contentCountByStatus.draft = params.value
                        }                        
                    } else {
                        let hasValue = false
                        if (params?.status==='active' ) {
                            hasValue = feed.contentCountByStatus?.active !== null && feed.contentCountByStatus?.active !== undefined
                        } else if (params?.status==='inreview') {
                            hasValue = feed.contentCountByStatus?.inreview !== null && feed.contentCountByStatus?.inreview !== undefined
                        } else if (params?.status==='draft') {
                            hasValue = feed.contentCountByStatus?.draft !== null && feed.contentCountByStatus?.draft !== undefined
                        }
                        if (!hasValue) {
                            await axios.get(`/api/content?page=1&itemPerPage=1&feeds=${params?.id}&status=${params?.status}`)
                            .then((response) => {
                                feed.contentCountByStatus = {
                                    active: params?.status==='active' ? response.data['hydra:totalItems'] : (feed.contentCountByStatus?.active ?? null),
                                    inreview: params?.status==='inreview' ? response.data['hydra:totalItems'] : (feed.contentCountByStatus?.inreview ?? null),
                                    draft: params?.status==='draft' ? response.data['hydra:totalItems'] : (feed.contentCountByStatus?.draft ?? null),
                                }    
                            })
                        }
                    }         
                    return feed
                } else {
                    throw new Error(`Feed not found: ${params.id}`)
                }
            }
        },

        async addFeed(_, params) {
            await axios.post('/api/feeds', params)
        },

        async updateFeed(_, params) {
            await axios.put(`/api/feeds/${params.id}`, params)            
        },

        async deleteFeed(_, id) {            
            await axios.delete(`/api/feeds/${id}`)
        },

        async pullFeed(_, id) {            
            await axios.put(`/api/feeds/${id}/pull`, {})
        },

        async resetFeed(_, id) {            
            await axios.put(`/api/feeds/${id}/reset`, {})
        },
    },

    mutations: {
        setFeed(state: any, feed) {
            state.feed = feed
        },
        setTotalRows(state: any, rows: number) {
            state.totalRows = rows
        },
    },
}

export default feedModule
